import { ReactNode, useEffect, useRef, useState } from 'react'
import type { ViewStyle } from 'react-native'
import {
  Accordion,
  YStack,
  XStack,
  Text,
  View,
  isWeb,
  type TamaguiElement,
} from 'tamagui'
import { useTranslation } from 'react-i18next'
import { useToastController } from '@tamagui/toast'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown'
import { faCirclePlay } from '@fortawesome/pro-light-svg-icons/faCirclePlay'
import { faFile } from '@fortawesome/pro-light-svg-icons/faFile'
import { faFileSignature } from '@fortawesome/pro-light-svg-icons/faFileSignature'
import { faFileWord } from '@fortawesome/pro-light-svg-icons/faFileWord'
import { faImage } from '@fortawesome/pro-light-svg-icons/faImage'
import { faMicrophone } from '@fortawesome/pro-light-svg-icons/faMicrophone'
import { faVideo } from '@fortawesome/pro-light-svg-icons/faVideo'
import {
  IProductCurriculumModule,
  IProductCurriculumPage,
  IProductLesson,
} from '@dtos/ProductPage'
import { Button } from '@components/Button'
import { useThemeColor } from '@hooks/useThemeColor'

interface ICurriculumAccordionProps {
  initialPage: IProductCurriculumPage
  onLoadMore: (page: number) => Promise<IProductCurriculumPage | null>
}

// O driver de animação do Tamagui não emite transição no bundle web deste app
// (transition-duration fica 0s), então a abertura/fechamento e o giro do
// chevron são feitos com transição CSS — o mesmo caminho já usado no aside
// sticky, que o RN Web repassa direto para o DOM.
const HEIGHT_TRANSITION = {
  transition: 'height 260ms ease',
} as unknown as ViewStyle

// Substitui o Accordion.HeightAnimator: o onLayout que ele usa para medir o
// conteúdo não dispara no web (a altura fica presa em 0 e, com overflow
// hidden, nada aparece ao abrir). Aqui a medição é do DOM (scrollHeight, com
// ResizeObserver para conteúdo que muda de tamanho depois — fontes, imagens).
function CollapsibleBox({
  open,
  children,
}: {
  open: boolean
  children: ReactNode
}) {
  const innerRef = useRef<TamaguiElement | null>(null)
  const [contentHeight, setContentHeight] = useState(0)

  useEffect(() => {
    if (!isWeb) return
    const element = innerRef.current as unknown as HTMLElement | null
    if (!element) return

    const measure = () => setContentHeight(element.scrollHeight)
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  if (!isWeb) {
    return <YStack display={open ? 'flex' : 'none'}>{children}</YStack>
  }

  return (
    <View
      overflow="hidden"
      height={open ? contentHeight : 0}
      style={HEIGHT_TRANSITION}
    >
      <View ref={innerRef}>{children}</View>
    </View>
  )
}

const CHEVRON_TRANSITION = {
  transition: 'transform 200ms ease',
} as unknown as ViewStyle

// Mesma semântica do utils/lessonMediaType.ts, com IconDefinition importado
// direto (a página é pública e não passa pelo library.add do HeroIcon).
const MEDIA_TYPE_ICONS: Record<string, IconDefinition> = {
  Video: faCirclePlay,
  Audio: faMicrophone,
  Text: faFileWord,
  Document: faFile,
  Image: faImage,
  WebinarHerospark: faVideo,
  LiveStream: faVideo,
  Quiz: faFileSignature,
}

// Linha de aula do hero_theme (productPage_module_lesson): ícone do tipo de
// mídia na cor de destaque à esquerda do título.
function LessonRow({
  lesson,
  accentColor,
}: {
  lesson: IProductLesson
  accentColor: string
}) {
  return (
    <XStack gap={8} alignItems="center">
      <FontAwesomeIcon
        icon={MEDIA_TYPE_ICONS[lesson.mediaType ?? ''] ?? faFile}
        size={16}
        color={accentColor}
      />
      <Text
        color="$text-primary"
        fontSize={16}
        fontFamily="$mulishRegular"
        flexShrink={1}
      >
        {lesson.title}
      </Text>
    </XStack>
  )
}

function ModuleLessons({
  module,
  accentColor,
}: {
  module: IProductCurriculumModule
  accentColor: string
}) {
  return (
    <YStack gap={12} paddingBottom={16}>
      {module.lessons.map((lesson) => (
        <LessonRow key={lesson.id} lesson={lesson} accentColor={accentColor} />
      ))}
      {module.subModules.map((subModule) => (
        <YStack key={subModule.id} gap={12} paddingLeft={16}>
          <Text
            color="$text-primary"
            fontSize={16}
            fontFamily="$mulishBold"
            fontWeight="700"
          >
            {subModule.name}
          </Text>
          {subModule.lessons.map((lesson) => (
            <LessonRow
              key={lesson.id}
              lesson={lesson}
              accentColor={accentColor}
            />
          ))}
        </YStack>
      ))}
    </YStack>
  )
}

function ModuleItem({
  module,
  open,
  accentColor,
  textColor,
}: {
  module: IProductCurriculumModule
  open: boolean
  accentColor: string
  textColor: string
}) {
  return (
    <Accordion.Item
      value={module.id}
      borderBottomWidth={1}
      borderBottomColor="$border-muted"
    >
      <Accordion.Trigger
        unstyled
        backgroundColor="transparent"
        borderWidth={0}
        paddingHorizontal={0}
        paddingVertical={16}
        cursor="pointer"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {() => (
          <>
            <Text
              color="$text-primary"
              fontSize={20}
              fontFamily="$mulishBold"
              fontWeight="700"
              textAlign="left"
              flexGrow={1}
              flexShrink={1}
            >
              {module.name}
            </Text>
            <View
              rotate={open ? '180deg' : '0deg'}
              style={isWeb ? CHEVRON_TRANSITION : undefined}
              flexShrink={0}
            >
              <FontAwesomeIcon
                icon={faChevronDown}
                size={14}
                color={textColor}
              />
            </View>
          </>
        )}
      </Accordion.Trigger>
      {/* forceMount mantém o conteúdo montado (role=region do Content) para a
          altura já estar medida quando o módulo abre. */}
      <CollapsibleBox open={open}>
        <Accordion.Content
          unstyled
          forceMount
          backgroundColor="transparent"
          padding={0}
        >
          <ModuleLessons module={module} accentColor={accentColor} />
        </Accordion.Content>
      </CollapsibleBox>
    </Accordion.Item>
  )
}

// Accordion no visual do hero_theme (productPage_modules): título 20/700,
// linha hairline embaixo de cada módulo e chevron à direita.
export function CurriculumAccordion({
  initialPage,
  onLoadMore,
}: ICurriculumAccordionProps) {
  const { t } = useTranslation('ProductPage')
  const toast = useToastController()
  const [modules, setModules] = useState(initialPage.nodes)
  const [currentPage, setCurrentPage] = useState(initialPage.currentPage)
  const [openModules, setOpenModules] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const accentColor = useThemeColor('accent')
  const textColor = useThemeColor('icon-primary')
  const hasMore = currentPage < initialPage.totalPages

  const handleLoadMore = async () => {
    setLoading(true)
    try {
      const page = await onLoadMore(currentPage + 1)
      if (!page) return

      setModules((previous) => [...previous, ...page.nodes])
      setCurrentPage(page.currentPage)
    } catch {
      // Sem o catch a página que falha vira unhandled rejection e o visitante
      // só vê o botão parar de girar, sem saber que não carregou.
      toast.show(t('curriculum.load-more-error'), { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (!modules.length) return null

  return (
    <YStack width="100%" testID="product-curriculum">
      {/* Controlado para o CollapsibleBox saber quais módulos estão abertos. */}
      <Accordion
        width="100%"
        type="multiple"
        value={openModules}
        onValueChange={setOpenModules}
      >
        {modules.map((module) => (
          <ModuleItem
            key={module.id}
            module={module}
            open={openModules.includes(module.id)}
            accentColor={accentColor}
            textColor={textColor}
          />
        ))}
      </Accordion>

      {hasMore && (
        <Button
          link
          size="small"
          alignSelf="center"
          marginTop={24}
          minHeight={0}
          loading={loading}
          onPress={handleLoadMore}
        >
          <Text
            color="$accent"
            fontSize={14}
            fontFamily="$mulishBold"
            fontWeight="700"
            textDecorationLine="underline"
          >
            {t('curriculum.load-more')}
          </Text>
        </Button>
      )}
    </YStack>
  )
}
